import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { useDispatch } from 'react-redux'

import ENV_VARS from '@/config'
import { colors } from '@/styles'
import useRole from '@/hooks/use-role'
import { Button } from '@/components/button'
import { WarningIconTriangle } from '@/components/icons'
import { showWarningModalWithCloseButton } from '@/components/warning-modal'

import { SectionWrapper } from '../elements'

const { DASHBOARD_URL } = ENV_VARS

const OuterWrapper = styled.div`
  background: ${colors.blue96};
`

const Wrapper = styled(SectionWrapper)`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const Title = styled.h2`
  font-family: Inter, sans-serif;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: ${colors.grey08};
  text-align: center;
  margin: 0 0 16px 0;
`

const Subtitle = styled.p`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${colors.grey08};
  text-align: center;
  max-width: 620px;
  margin: 0 auto 40px auto;
`

const customButtonStyle = css`
  width: fit-content;
  align-self: center;
`

const GetStarted = () => {
  const { t } = useTranslation(['translation', 'landlords'])
  const role = useRole()
  const isLandlord = role === 'landlord'
  const isTenant = role === 'tenant'
  const dispatch = useDispatch()
  const logout = () => dispatch.auth.logout()

  const router = useRouter()

  const handleClickPostAListing = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    if (isTenant) {
      showWarningModalWithCloseButton({
        title: t(
          'r.landlord.view.logged_in_as_renter.title',
          "Oops! It looks like you're logged in as a renter."
        ),
        message: t(
          'r.landlord.view.logged_in_as_renter.text',
          'To access landlord-specific features, please log out and sign up for a landlord account or log in with your landlord account details.'
        ),
        icon: WarningIconTriangle,
        iconBgColor: '#FCF5D7',
        button: {
          text: t('common.log_out', 'Log Out'),
          onClick: logout
        }
      })
    } else {
      isLandlord
        ? (window.location.href = `${DASHBOARD_URL}/listing/create`)
        : router.push({
            pathname: '/post-a-listing',
            query: { accountType: 'essentials' }
          })
    }
  }

  return (
    <OuterWrapper>
      <Wrapper>
        <Title>{t('landlords:Try for free, forever.')}</Title>
        <Subtitle>
          {t(
            `landlords:Unleash your rental business's potential with access to essential tools and find the perfect tenant.`
          )}
        </Subtitle>
        <Button
          id="landlord-get-started-footer-click"
          customStyle={customButtonStyle}
          theme="primary"
          onClick={handleClickPostAListing}
        >
          {t('landlords:GET STARTED')}
        </Button>
      </Wrapper>
    </OuterWrapper>
  )
}

export default GetStarted
